import { Link } from "react-router-dom";
import {
  MessageSquare,
  FileText,
  Edit,
  Subtitles,
  Building,
  Globe,
  CheckCircle,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function ServicesPage() {
  const services = [
    {
      icon: <MessageSquare style={{ width: '2rem', height: '2rem' }} />,
      title: "Professional Interpretation",
      description:
        "Expert interpretation services for Korean, German, Japanese, French, and Spanish. Available both on-site and remote for seamless global communication.",
      features: [
        "On-site interpretation for meetings and events",
        "Remote interpretation via video conferencing",
        "Simultaneous and consecutive interpretation",
        "Specialized terminology expertise",
      ],
      industries: [
        "Business & Corporate",
        "Legal & Judicial",
        "Medical & Healthcare",
        "Diplomatic & Government",
      ],
      benefits: [
        "Real-time accurate translation",
        "Cultural context and sensitivity",
        "Professional confidentiality",
        "Technical expertise in specialized fields",
      ],
      colorClass: "blue",
    },
    {
      icon: <FileText style={{ width: '2rem', height: '2rem' }} />,
      title: "Translation Services",
      description:
        "Comprehensive translation solutions for technical, legal, business, academic content, and website localization. Fast, accurate, and confidential service guaranteed.",
      features: [
        "Document translation (legal, technical, academic)",
        "Website localization and content adaptation",
        "Marketing material translation",
        "Certified translation services",
      ],
      industries: [
        "Legal & Contracts",
        "Technical & Engineering",
        "Business & Marketing",
        "Academic & Research",
      ],
      benefits: [
        "Native speaker expertise",
        "Industry-specific knowledge",
        "Quality assurance processes",
        "Quick turnaround times",
      ],
      colorClass: "green",
    },
    {
      icon: <Edit style={{ width: '2rem', height: '2rem' }} />,
      title: "Editing & Content Moderation",
      description:
        "Professional proofreading and polishing of translated documents. Multilingual content moderation to ensure accuracy, cultural appropriateness, and brand consistency.",
      features: [
        "Proofreading and editing translated content",
        "Cultural appropriateness review",
        "Style guide compliance",
        "Multilingual content moderation",
      ],
      industries: [
        "Publishing & Media",
        "Digital Marketing",
        "E-commerce",
        "Social Media Platforms",
      ],
      benefits: [
        "Enhanced content quality",
        "Cultural sensitivity assurance",
        "Brand voice consistency",
        "Error-free communications",
      ],
      colorClass: "purple",
    },
    {
      icon: <Subtitles style={{ width: '2rem', height: '2rem' }} />,
      title: "Transcription & Subtitling",
      description:
        "Professional subtitle creation for videos, webinars, documentaries, and corporate training materials. Accurate transcription services in multiple languages.",
      features: [
        "Video subtitle creation",
        "Audio transcription services",
        "Corporate training material subtitling",
        "Documentary and media subtitling",
      ],
      industries: [
        "Media & Entertainment",
        "Education & Training",
        "Corporate Communications",
        "Digital Marketing",
      ],
      benefits: [
        "Improved accessibility",
        "Global content reach",
        "Professional formatting",
        "Multiple file format support",
      ],
      colorClass: "orange",
    },
    {
      icon: <Building style={{ width: '2rem', height: '2rem' }} />,
      title: "B2B Language Solutions",
      description:
        "Comprehensive language solutions tailored for businesses including corporate training, business event interpretation, translation services, and L&D subtitling.",
      features: [
        "Corporate language training programs",
        "Business event interpretation",
        "Translation and localization for businesses",
        "L&D content subtitling",
      ],
      industries: [
        "Multinational Corporations",
        "International Trade",
        "Technology Companies",
        "Manufacturing & Export",
      ],
      benefits: [
        "Enhanced global communication",
        "Improved team productivity",
        "Market expansion support",
        "Cultural competency development",
      ],
      colorClass: "indigo",
    },
  ];

  const languages = [
    {
      name: "Korean",
      flag: "🇰🇷",
      specialty: "Technology, Manufacturing, International Trade",
    },
    {
      name: "German",
      flag: "🇩🇪",
      specialty: "Engineering, Automotive, Business",
    },
    {
      name: "Japanese",
      flag: "🇯🇵",
      specialty: "Technology, Manufacturing, Business Etiquette",
    },
    {
      name: "French",
      flag: "🇫🇷",
      specialty: "Diplomacy, International Business, African Markets",
    },
    {
      name: "Spanish",
      flag: "🇪🇸",
      specialty: "Latin American Markets, International Trade",
    },
  ];

  return (
    <div className="services-page">
      <Header />

      {/* Page Header */}
      <section className="page-header">
        <div className="header-content">
          <h1><span className="first-word">Our Services</span> </h1>
          <p>Professional language services for global business success</p>
        </div>
      </section>

      {/* Services Introduction */}
      <section className="intro-section">
        <div className="section-content">
          <h2>Comprehensive Language Solutions</h2>
          <p>
            From interpretation and translation to content moderation and
            corporate training, we provide end-to-end language services that
            enable seamless global communication and business growth.
          </p>
        </div>
      </section>

      {/* Languages We Support */}
      <section className="languages-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Languages We Support</h2>
            <p>
              Expert services in five major business languages with specialized
              industry knowledge
            </p>
          </div>
          <div className="languages-grid">
            {languages.map((language, index) => (
              <div key={index} className="language-card">
                <div className="flag">{language.flag}</div>
                <h3>{language.name}</h3>
                <p>{language.specialty}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      {services.map((service, index) => {
        const serviceId = service.title.toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        return (
        <section
          key={index}
          id={serviceId}
          className={`service-detail-section ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="section-content">
            <div className={`service-card ${service.colorClass}`}>
              <div className="service-content">
                <div className="service-overview">
                  <div className="service-header">
                    <div className={`icon-wrapper ${service.colorClass}`}>
                      {service.icon}
                    </div>
                    <h2>{service.title}</h2>
                  </div>
                  <p>{service.description}</p>

                  <div className="features-list">
                    <h3>Service Features:</h3>
                    <ul>
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex}>
                          <CheckCircle className="check-icon" style={{ width: '1.25rem', height: '1.25rem', color: '#22c55e' }} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="service-details">
                  <div className="industries">
                    <h3>Industries Supported:</h3>
                    <div className="industries-grid">
                      {service.industries.map((industry, industryIndex) => (
                        <div key={industryIndex} className="industry-badge">
                          <span>{industry}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="benefits">
                    <h3>Key Benefits:</h3>
                    <ul>
                      {service.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex}>
                          <CheckCircle className="check-icon" style={{ width: '1.25rem', height: '1.25rem', color: '#22c55e' }} />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="service-actions">
                    <Link to="/contact#contact-form" className="btn btn-primary">
                      Get Quote
                    </Link>
                    <Link to="/contact#contact-form" className="btn btn-secondary">
                      Learn More
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        );
      })}

      {/* Service Process */}
      <section className="process-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Our Service Process</h2>
            <p>Simple, efficient workflow designed for professional results</p>
          </div>
          <div className="process-grid">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Consultation</h3>
              <p>Discuss your specific needs and project requirements</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Quote & Timeline</h3>
              <p>Receive detailed proposal with pricing and delivery schedule</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Expert Execution</h3>
              <p>Our certified professionals deliver high-quality results</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Quality Assurance</h3>
              <p>Review and refinement to ensure perfect results</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="why-choose-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Why Choose Our Services</h2>
            <p>Professional excellence that drives business success</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="icon-wrapper yellow">
                <Globe style={{ width: '2rem', height: '2rem', color: '#ca8a04' }} />
              </div>
              <h3>Global Expertise</h3>
              <p>Native speakers with deep cultural and industry knowledge</p>
            </div>
            <div className="benefit-card">
              <div className="icon-wrapper green">
                <CheckCircle style={{ width: '2rem', height: '2rem', color: '#16a34a' }} />
              </div>
              <h3>Quality Guaranteed</h3>
              <p>Rigorous quality assurance processes ensure accuracy</p>
            </div>
            <div className="benefit-card">
              <div className="icon-wrapper blue">
                <MessageSquare style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />
              </div>
              <h3>Fast Turnaround</h3>
              <p>Efficient workflows deliver results on time, every time</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}